import { HttpErrorResponse } from '@angular/common/http';
import {
    HTTP_REQUEST_METADATA_KEY,
    HttpErrorResponseWithMetadata,
} from '@core/interceptors/sentry-http.interceptor';

/**
 * Custom error class for HTTP errors that provides better Sentry grouping.
 * Errors are grouped by URL path (without query params) and status code.
 *
 * Includes request metadata (method, body) if available from the interceptor.
 */
export class HttpError extends Error {
    readonly status: number;
    readonly statusText: string;
    readonly url: string;
    readonly responseBody: unknown;
    readonly requestMethod: string;
    readonly requestBody: unknown;

    constructor(httpErrorResponse: HttpErrorResponseWithMetadata) {
        // Extract the path from the URL (remove query params for better grouping)
        const urlPath = extractUrlPath(httpErrorResponse.url || 'unknown');

        // Create a descriptive message that includes status and URL
        const message = `HTTP ${httpErrorResponse.status} ${httpErrorResponse.statusText || 'Error'}: ${urlPath}`;
        super(message);

        this.name = 'HttpError';
        this.status = httpErrorResponse.status;
        this.statusText = httpErrorResponse.statusText;
        this.url = httpErrorResponse.url || 'unknown';
        this.responseBody = httpErrorResponse.error;

        // Extract request metadata if attached by interceptor
        const metadata = httpErrorResponse[HTTP_REQUEST_METADATA_KEY];
        this.requestMethod = metadata?.method || 'UNKNOWN';
        this.requestBody = metadata?.body;

        if (httpErrorResponse['stack']) {
            this.stack = httpErrorResponse['stack'];
        }
    }
}

/**
 * Extracts the path portion from a URL, removing query parameters and fragments.
 * This helps with grouping similar errors together in Sentry.
 *
 * @example
 * extractUrlPath('https://api.example.com/users/123?foo=bar') // returns '/users/123'
 * extractUrlPath('https://api.example.com/trips/456/book') // returns '/trips/456/book'
 */
export function extractUrlPath(url: string): string {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.pathname;
    } catch {
        // If URL parsing fails, try to extract path manually
        const pathMatch = url.match(/https?:\/\/[^/]+(\/[^?#]*)/);
        return pathMatch?.[1] || url;
    }
}

/**
 * Checks if an error is an Angular HttpErrorResponse.
 */
function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
    return (
        error !== null &&
        typeof error === 'object' &&
        'status' in error &&
        'statusText' in error &&
        'url' in error &&
        'name' in error &&
        (error as HttpErrorResponse).name === 'HttpErrorResponse'
    );
}

/**
 * Custom error extractor for Sentry that properly handles Angular HttpErrorResponse.
 *
 * When an HttpErrorResponse is captured, this extractor converts it to a proper
 * Error object that Sentry can understand and group correctly.
 *
 * Errors are grouped by:
 * - URL path (without query parameters)
 * - HTTP status code
 *
 * @example
 * Sentry.createErrorHandler({
 *     extractor: httpErrorExtractor,
 * });
 */
export function httpErrorExtractor(
    error: unknown,
    defaultExtractor: (error: unknown) => unknown
): unknown {
    // First, use the default extractor to unwrap any Angular error wrapping
    const extractedError = defaultExtractor(error);

    // Check if the extracted error is an HttpErrorResponse
    if (isHttpErrorResponse(extractedError)) {
        return new HttpError(extractedError);
    }

    // Also check the original error in case it wasn't properly extracted
    if (isHttpErrorResponse(error)) {
        return new HttpError(error);
    }

    // For all other errors, return the default extracted error
    return extractedError;
}
